/**
 * DataSource: IA embarcada (M11)
 * ------------------------------
 * Hoje: mock determinístico (não consome tokens) + Edge Function
 *       `ai-triage` chamando Anthropic API.
 * Futuro: tudo. Telles: "Considere os modelos da Anthropic
 *         inicialmente."
 *
 * Política de classificação de dados: o que vai pro modelo nunca pode
 * ser 🔴 Restrito (CPF, salário). Leituras 🟠 Sensíveis vão pra Anthropic
 * (não Azure OpenAI Brasil) conforme decisão JFB Mai/2026.
 */

import type { TarpLevel, UserProfile } from '@/domain';
import { supabase } from '@/lib/supabase';
import type { DataSourceMeta } from './types';

export interface AiTriagemInput {
  perfil: UserProfile;
  pergunta: string;
  contexto?: {
    instrumentoCode?: string;
    valorLeitura?: number;
    levelClassificado?: TarpLevel | null;
  };
}

export interface AiTriagemResposta {
  resposta: string;
  severidadeSugerida: TarpLevel | null;
  podeSerFalhaSensor: boolean;
  /** modelo que respondeu (sonnet, haiku, mock) */
  modelo: string;
  /** custos / tokens — null se mock */
  usage: { inputTokens: number; outputTokens: number } | null;
}

export interface AiDataSource extends DataSourceMeta {
  triagem(input: AiTriagemInput): Promise<AiTriagemResposta>;
}

// ── Mock determinístico (zero custo) ────────────────────────
export const mockAi: AiDataSource = {
  mode: 'mock',
  pendingIntegration: {
    owner: 'TI JFB',
    name: 'Configuração da Edge Function ai-triage',
    eta: 'Próxima sprint',
    status: 'em_andamento',
    description:
      'Edge Function `ai-triage` esqueleto pronto. Aguardando ANTHROPIC_API_KEY ' +
      'em Supabase secrets pra ativar. POC usa respostas mock determinísticas.',
  },

  async triagem(input) {
    const pergunta = input.pergunta.toLowerCase();
    const level = input.contexto?.levelClassificado ?? null;

    if (pergunta.includes('falha') || pergunta.includes('sensor')) {
      return {
        resposta:
          'Verifique no M6 a frequência de leitura e o status de conexão do instrumento. ' +
          'Se a última leitura tem ≥ 6h, pode ser falha de comunicação. Compare com ' +
          'instrumentos vizinhos antes de classificar como anomalia real.',
        severidadeSugerida: 'ATENCAO',
        podeSerFalhaSensor: true,
        modelo: 'mock',
        usage: null,
      };
    }

    if (level === 'ALERTA' || level === 'EMERGENCIA') {
      return {
        resposta:
          `Leitura classificada como ${level}. Acione M3 Via 3 imediatamente — ` +
          'isso notifica RT Galvani + RT JFB simultaneamente. Não acione sirene ' +
          'sem confirmação técnica habilitada (R-B08).',
        severidadeSugerida: level,
        podeSerFalhaSensor: false,
        modelo: 'mock',
        usage: null,
      };
    }

    return {
      resposta:
        'Nenhum sinal de gravidade nos dados disponíveis. Mantenha rotina de ' +
        'inspeção quinzenal e registre observações no M10.',
      severidadeSugerida: level,
      podeSerFalhaSensor: false,
      modelo: 'mock',
      usage: null,
    };
  },
};

// ── Edge Function (Anthropic) ───────────────────────────────
export const anthropicEdgeAi: AiDataSource = {
  mode: 'integrated',

  async triagem(input) {
    const { data, error } = await supabase.functions.invoke<AiTriagemResposta>('ai-triage', {
      body: input,
    });
    if (error) throw error;
    if (!data) throw new Error('ai-triage retornou vazio');
    return data;
  },
};
